// THE KNOWLEDGE GRAPH'S CONNECTIONS for ONE item (busy-my-web#710, epic busy-intelligent-srv#35).
//
// Every standalone Busy item — a task, a note, an event, a document, a contact, an email thread,
// an Ongoing — is a NODE of busy-graph-srv, and what may belong with it is an EDGE: suggested by
// meaning, confirmed or ruled out by exact facts, the middle judged by a small model. The BFF serves
// them per item (`GET v1/connections/{type}/{refId}`, busy-main-bff#60):
//
//   { node: { type, refId, title, summary } | null,
//     connections: [ { edgeId, state, confidence, band, reason, reasons[], matchedFacts[{kind,
//                      display}], provenance, createdUtc, updatedUtc,
//                      node: { type, refId, title, summary, parentType, parentRefId } } ] }
//
// and components/Connections/ConnectionsPanel.vue draws them with the pieces the in-Ongoing table
// already has (Figma "Review email groups and match ongoings" 40015353:452042 and the older
// "possible connection" journey 40011046:161445):
//
//   · the SUGGESTED edges as the table rows Home/blocks/parts/ConnectionsTableView.vue draws (glyph
//     per node type, title, the "Why Busy suggests this:" reason, Last update, the Strong / Likely /
//     Possible chip, View / Not related / Connect, and the row's connecting / connected states);
//   · one edge's BRIEFING in Email/PossibleConnectionReviewCard.vue (summary, "Why Busy suggests
//     this connection" bullets, 1 of N, Connect);
//   · the CONFIRMED edges grouped by kind.
//
// This module is the reading of the payload into those shapes and nothing else — no store, no api,
// nothing mounted — so it is tested without mounting anything. The band thresholds, the chip
// palette, the "Sep 7" date and the open targets are NOT re-decided here: they are
// lib/possibleConnections' and lib/chatConnections' own, so a connection reads the same on every
// surface (busy-main-bff's Core/ConfidenceBands.cs mirrors the same 0.8 / 0.5 thresholds).

import { moduleColor } from '@/lib/moduleColors'
import { moduleIcon } from '@/lib/moduleIcons'
import { countLabel } from '@/lib/briefings/rows'
import { connectionDate } from '@/lib/possibleConnections'
import { bandChip } from '@/lib/chatConnections'

export const GRAPH_TEXT = {
  heading: 'Possible connections',
  subheading: 'Review suggested connections',
  // The row's reason label (frame 40015353:452115 "Why Busy suggests this: Same supplier and
  // invoice INV-1234 as this Ongoing.").
  whyLine: 'Why Busy suggests this:',
  // The briefing card's bullets heading (frame 40015353:452042, the "Briefing" card).
  whyHeading: 'Why Busy suggests this connection',
  briefing: 'Briefing',
  connections: 'Connections',
  connectFailed: "Couldn't connect. Try again in a moment.",
  rejectFailed: "Couldn't mark it as not related. Try again in a moment.",
  undoFailed: "Couldn't undo the connection.",
  // The user-wide list once its last suggestion has been answered.
  none: 'Nothing is waiting to be connected.',
}

/**
 * The node types busy-graph-srv knows (SPEC §1), each with the MODULE its glyph and colour are
 * drawn from and the noun the app calls it.
 *
 * An Ongoing and its parts draw the research globe — the Ongoing list's own glyph for a story (and
 * lib/chatConnections' `rowModule` reads `ongoing` the same way). A DOCUMENT is not one of the
 * module palette's seven; it takes the pack's `file` glyph in the contact grey, the neutral every
 * "not a module" thing in the app already wears.
 */
const NODE_TYPES = {
  email_thread: { module: 'email', noun: 'Email' },
  task: { module: 'task', noun: 'Task' },
  note: { module: 'note', noun: 'Note' },
  event: { module: 'event', noun: 'Event' },
  contact: { module: 'contact', noun: 'Contact' },
  document: { module: 'document', noun: 'Document' },
  ongoing: { module: 'research', noun: 'Ongoing' },
  research_pack: { module: 'research', noun: 'Research pack' },
  focus_item: { module: 'research', noun: 'Focus item' },
  follow_up: { module: 'task', noun: 'Follow-up' },
  topic: { module: 'research', noun: 'Topic' },
}

const DOCUMENT_GLYPH = { icon: 'file', color: '#4B5563' }

/** The order confirmed groups are listed in — the in-Ongoing table's KIND_ORDER, widened. */
const GROUP_ORDER = ['ongoing', 'email_thread', 'task', 'note', 'event', 'research_pack', 'focus_item', 'follow_up', 'document', 'contact', 'topic']

const str = (v) => (v === null || v === undefined ? '' : String(v).trim())

/** A node type in the graph's own spelling (`email_thread`), whatever case or dashes it came in. */
export function nodeType(type) {
  return str(type).toLowerCase().replace(/[\s-]+/g, '_')
}

/** What a node type is CALLED ("Task", "Research pack"); an unknown type is an "Item". */
export function nodeNoun(type) {
  return NODE_TYPES[nodeType(type)]?.noun || 'Item'
}

/** A node type's glyph + colour (`{ module, icon, color }`). */
export function nodeGlyph(type) {
  const module = NODE_TYPES[nodeType(type)]?.module || 'research'
  if (module === 'document') return { module, ...DOCUMENT_GLYPH }
  return { module, icon: moduleIcon(module), color: moduleColor(module) }
}

/** The node an edge points at, in one shape whether the BFF spoke camelCase or snake_case. */
export function normalizeNode(raw) {
  if (!raw || typeof raw !== 'object') return null
  const type = nodeType(raw.type)
  const refId = str(raw.refId ?? raw.ref_id)
  if (!type || !refId) return null
  return {
    type,
    refId,
    title: str(raw.title),
    summary: str(raw.summary),
    parentType: nodeType(raw.parentType ?? raw.parent_type) || null,
    parentRefId: str(raw.parentRefId ?? raw.parent_ref_id) || null,
  }
}

const list = (v) => (Array.isArray(v) ? v : [])

/** One edge, normalised; null when it names no edge or no node to show. */
export function normalizeEdge(raw) {
  if (!raw || typeof raw !== 'object') return null
  const edgeId = str(raw.edgeId ?? raw.edge_id ?? raw.id)
  const node = normalizeNode(raw.node)
  if (!edgeId || !node) return null
  const confidence = Number(raw.confidence)
  return {
    edgeId,
    state: str(raw.state).toLowerCase() || 'suggested',
    confidence: Number.isFinite(confidence) ? confidence : null,
    band: str(raw.band).toLowerCase(),
    reason: str(raw.reason),
    reasons: list(raw.reasons).map(str).filter(Boolean),
    matchedFacts: list(raw.matchedFacts ?? raw.matched_facts)
      .filter((f) => f && typeof f === 'object')
      .map((f) => ({ kind: str(f.kind), display: str(f.display) }))
      .filter((f) => f.display),
    provenance: str(raw.provenance),
    createdUtc: raw.createdUtc ?? raw.created_utc ?? null,
    updatedUtc: raw.updatedUtc ?? raw.updated_utc ?? null,
    node,
    // The OTHER side, on the user-wide list only (`GET v1/connections?state=suggested&type=…`):
    // the item the suggestion is about — the email, on the Email workspace's list. Null elsewhere.
    source: normalizeNode(raw.source ?? raw.sourceNode ?? raw.source_node),
  }
}

/**
 * The BFF's answer, unwrapped: `{ node, connections }`. Takes the axios response or its body, the
 * BFF's `{ data: … }` envelope or not, and a bare array of connections, so a change of envelope on
 * the server side never empties the panel. Rejected edges are never drawn.
 */
export function normalizeConnections(payload) {
  let body = payload?.data !== undefined && payload?.status !== undefined ? payload.data : payload
  if (body && !Array.isArray(body) && body.data !== undefined && body.connections === undefined) body = body.data
  const raw = Array.isArray(body) ? body : list(body?.connections)
  const seen = new Set()
  const connections = []
  for (const item of raw) {
    const edge = normalizeEdge(item)
    if (!edge || edge.state === 'rejected' || seen.has(edge.edgeId)) continue
    seen.add(edge.edgeId)
    connections.push(edge)
  }
  return { node: Array.isArray(body) ? null : normalizeNode(body?.node), connections }
}

const byConfidence = (a, b) => (b.confidence ?? -1) - (a.confidence ?? -1)

/** The suggestions, strongest first (the server's own order is confirmed-first, then confidence). */
export function suggestedEdges(edges) {
  return list(edges).filter((e) => e && e.state === 'suggested').sort(byConfidence)
}

/** The confirmed edges, strongest first. */
export function confirmedEdges(edges) {
  return list(edges).filter((e) => e && e.state === 'confirmed').sort(byConfidence)
}

/** The edge's chip — the server's band where it sent one, else its score (lib/chatConnections). */
export function edgeChip(edge) {
  return bandChip({ band: edge?.band, confidence: edge?.confidence })
}

/** The edge's one-line why: the judge's reason, else its first bullet, else its first matched fact. */
export function edgeReason(edge) {
  return str(edge?.reason) || str(edge?.reasons?.[0]) || str(edge?.matchedFacts?.[0]?.display)
}

/**
 * The "Why Busy suggests this connection" bullets: the server's `reasons`, the one-line reason
 * first when the list does not already say it, then the matched facts in words — deduped and in
 * the server's order. Empty when nothing was recorded, so the card leaves the heading off.
 */
export function edgeWhyLines(edge) {
  const out = []
  const seen = new Set()
  const push = (line) => {
    const text = str(line)
    if (!text || seen.has(text.toLowerCase())) return
    seen.add(text.toLowerCase())
    out.push(text)
  }
  if (!list(edge?.reasons).length) push(edge?.reason)
  for (const line of list(edge?.reasons)) push(line)
  for (const fact of list(edge?.matchedFacts)) push(fact.display)
  return out
}

/**
 * The SUMMARY as lines: the enrich step writes one short paragraph (≤ 400 chars, SPEC §4); it is
 * shown as the frame's bullets one sentence each — or as the lines the summary already has, when
 * it was written as a list.
 */
export function summaryLines(summary) {
  const text = str(summary)
  if (!text) return []
  const lines = text.split(/\n+/).map((l) => l.replace(/^\s*(?:[-•*]|\d+[.)])\s+/, '').trim()).filter(Boolean)
  if (lines.length > 1) return lines
  return text.split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/).map((s) => s.trim()).filter(Boolean)
}

const edgeDate = (edge, now) => connectionDate(edge?.updatedUtc || edge?.createdUtc, now)

/**
 * The SUGGESTED edges as ConnectionsTableView rows (`{ key, row, title, module, icon, color,
 * reason, date, chip, state }`). `states` holds this panel's in-flight row states by edge id
 * ('connecting'), so a row being connected shows the table's own three-dot loader.
 */
export function tableRows(edges, states = {}, now = new Date()) {
  return suggestedEdges(edges).map((edge) => {
    // A user-wide row (it carries its `source`) is NAMED by its source — the email the question is
    // about — and says what it may belong to in front of the why: "Ongoing Kitchen renovation ·
    // Same supplier and invoice INV-1234" (frame 40015353:452115's "Kitchen renovation · Ongoing /
    // Why Busy suggests this: …", in the table's one reason cell).
    const named = edge.source || edge.node
    const glyph = nodeGlyph(named.type)
    const reason = edgeReason(edge)
    const target = edge.source ? `${nodeNoun(edge.node.type)} ${edge.node.title || ''}`.trim() : ''
    return {
      key: edge.edgeId,
      row: edge,
      title: named.title || nodeNoun(named.type),
      module: glyph.module,
      icon: glyph.icon,
      color: glyph.color,
      reason: [target, reason].filter(Boolean).join(' · '),
      date: edgeDate(edge, now),
      chip: edgeChip(edge),
      state: str(states?.[edge.edgeId]),
    }
  })
}

/**
 * One edge as PossibleConnectionReviewCard's candidate — the in-Ongoing table's row shape
 * (lib/possibleConnections `connectionRow`), filled from the graph's edge:
 *   · `summary` / `summaryLines` are new on the card and drawn when present (the graph enriches
 *     every node, which the old suggestion payload never did);
 *   · `story` stays '' — the card's "To <Ongoing>" chip names where a candidate is offered TO, and
 *     here that is the item being read; the briefing's pair strip says it instead (`briefingPair`).
 */
export function reviewCandidate(edge, now = new Date()) {
  if (!edge?.node) return null
  const glyph = nodeGlyph(edge.node.type)
  const facts = [nodeNoun(edge.node.type), ...list(edge.matchedFacts).map((f) => f.display), edgeDate(edge, now)]
  return {
    id: edge.edgeId,
    name: edge.node.title || nodeNoun(edge.node.type),
    module: glyph.module,
    icon: glyph.icon,
    color: glyph.color,
    isEmail: edge.node.type === 'email_thread',
    entityId: edge.node.refId,
    confidence: edgeChip(edge),
    why: edgeWhyLines(edge),
    facts: facts.filter(Boolean).slice(1).join(' · '),
    noun: nodeNoun(edge.node.type),
    summary: edge.node.summary,
    summaryLines: summaryLines(edge.node.summary),
    story: '',
    sender: '',
    received: null,
    card: null,
  }
}

/**
 * The briefing's pair strip (frame 40015353:452042: "Supplier A Ltd · 14 Oct / Invoice INV-1234 ›
 * ✓ Kitchen renovation / Ongoing · 2 tasks"): the CANDIDATE on the left, the item being read on the
 * right, each with its glyph, title and kind. Null when the item itself is unknown to the graph.
 */
export function briefingPair(edge, self) {
  const me = normalizeNode(self)
  if (!edge?.node || !me) return null
  const side = (node) => ({
    ...nodeGlyph(node.type),
    title: node.title || nodeNoun(node.type),
    sub: nodeNoun(node.type),
  })
  return { from: side(edge.node), to: side(me) }
}

/** "1 of 6" — the briefing's place in the suggestions it steps through. */
export function positionLabel(index, total) {
  const n = Number(total) || 0
  if (n <= 0) return ''
  const i = Math.min(Math.max(Number(index) || 0, 0), n - 1)
  return `${i + 1} of ${n}`
}

/** The neighbour the ‹ › arrows step to, wrapping round; -1 when there is nothing to step through. */
export function stepIndex(index, delta, total) {
  const n = Number(total) || 0
  if (n <= 0) return -1
  return (((Number(index) || 0) + (Number(delta) || 0)) % n + n) % n
}

/** "6 Possible connections" / "1 Possible connection" — the panel's heading; '' for none. */
export function suggestionsHeading(count) {
  const n = Number(count) || 0
  return n > 0 ? countLabel(n, 'Possible connection') : ''
}

/** "2 Connections" — the confirmed group's heading; '' for none. */
export function connectionsHeading(count) {
  const n = Number(count) || 0
  return n > 0 ? countLabel(n, 'Connection') : ''
}

/**
 * The CONFIRMED edges grouped by kind — "Tasks", "Emails" … — in GROUP_ORDER, each group's rows
 * strongest first. Unknown types are listed last under their own noun ("Items").
 */
export function connectionGroups(edges, now = new Date()) {
  const groups = new Map()
  for (const edge of confirmedEdges(edges)) {
    const type = edge.node.type
    if (!groups.has(type)) groups.set(type, [])
    const glyph = nodeGlyph(type)
    groups.get(type).push({
      key: edge.edgeId,
      edge,
      title: edge.node.title || nodeNoun(type),
      icon: glyph.icon,
      color: glyph.color,
      reason: edgeReason(edge),
      date: edgeDate(edge, now),
    })
  }
  const rank = (t) => {
    const i = GROUP_ORDER.indexOf(t)
    return i < 0 ? GROUP_ORDER.length : i
  }
  return [...groups.keys()]
    .sort((a, b) => rank(a) - rank(b))
    .map((type) => {
      const rows = groups.get(type)
      return { type, label: countLabel(rows.length, nodeNoun(type)), rows }
    })
}

/**
 * Where OPENING a connected item goes — the app's own single-page readers where the item has one
 * (Pouria, 2026-10-02: tasks, notes and events open "in a single page … same principle as email"):
 *   · task / note / event → its reader page (`/tasks|notes|events/item/:id`);
 *   · email thread → the thread's page (`thread`, opened through lib/threadReader so the email
 *     session is entered the way every other opener enters it);
 *   · Ongoing → its story; a research pack → its Ongoing focused on it; a focus item or follow-up →
 *     its Ongoing;
 *   · contact → the box (`smartbox-edit-entity`, the contact list's own opener);
 *   · document → the file's Briefing card (`fileBriefing/open`).
 * Null when there is nothing to open.
 */
const READER_PATH = { task: 'tasks', note: 'notes', event: 'events' }

export function connectionOpenTarget(node) {
  const n = normalizeNode(node)
  if (!n) return null
  const id = encodeURIComponent(n.refId)
  if (READER_PATH[n.type]) return { kind: 'route', path: `/${READER_PATH[n.type]}/item/${id}` }
  if (n.type === 'email_thread') return { kind: 'thread', row: { timelineId: n.refId, subject: n.title } }
  if (n.type === 'ongoing') return { kind: 'route', path: `/ongoing/${id}` }
  if (n.parentType === 'ongoing' && n.parentRefId) {
    const home = `/ongoing/${encodeURIComponent(n.parentRefId)}`
    return { kind: 'route', path: n.type === 'research_pack' ? `${home}?pack=${id}` : home }
  }
  if (n.type === 'contact') return { kind: 'entity', detail: { menu: 'contacts', id: n.refId, title: n.title } }
  if (n.type === 'document') return { kind: 'document', file: { id: n.refId, name: n.title } }
  return null
}

/** The green notice after a connect: "Connected to Kitchen renovation" (frame 40015353:452042). */
export function connectedText(edge) {
  const title = str(edge?.node?.title)
  return title ? `Connected to ${title}` : 'Connected'
}

/** …and after a "Not related": "Kitchen renovation · Marked not related". */
export function rejectedText(edge) {
  const title = str(edge?.node?.title)
  return title ? `${title} · Marked not related` : 'Marked not related'
}

/**
 * The Email workspace Briefing's possible-connections record, when the GRAPH answers it
 * (busy-my-web#710): `{ count, open, graph: true }` over the user-wide email suggestions, or null to
 * leave the older "Possible Ongoing matches" row in charge — an old BFF (404), a list not read yet,
 * or a graph with nothing to suggest while the older matches still have some (`legacyCount`). An
 * open list whose last suggestion was just answered stays the graph's, so the middle does not
 * swap to the other list under the user.
 */
export function graphMatches(rootGetters, open = false, legacyCount = 0) {
  const read = rootGetters?.['graphConnections/inbox']
  if (typeof read !== 'function') return null
  const entry = read('email_thread')
  if (!entry || entry.status !== 'ready') return null
  const count = suggestedEdges(entry.connections).length
  if (count <= 0 && !(open && !(Number(legacyCount) > 0))) return null
  return { count, open: !!open, graph: true }
}

/** The store key the user-wide suggestions of one type are filed under. */
export function inboxKey(type = 'email_thread') {
  return `inbox:${nodeType(type) || 'email_thread'}`
}

/** The store key one panel's state is filed under. */
export function panelKey(type, refId) {
  const t = nodeType(type)
  const id = str(refId)
  return t && id ? `${t}:${id}` : ''
}

export default tableRows
